import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Avatar,
    Paper,
    FormHelperText,
    Chip,
    Stack,
    InputAdornment,
    CircularProgress,
    IconButton,
    Alert,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    Work as WorkIcon,
    AccountBalance as BankIcon,
    Badge as BadgeIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Lock as LockIcon,
    VpnKey as VpnKeyIcon,
    AdminPanelSettings as RoleIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { request } from '../../../../../../utils/request';
import Brandcrumb from '../../../../../../componentsUiBlock/Brandcrumb';
import { ContactPhone, EmployeeFormData } from '../types';
import { baseUrlImage } from '../../../../../../utils/apiClient';
import { useImageCompression } from '../../../../../../utils/useImageCompression';

// Add Role interface
interface Role {
    _id: string;
    name: string;
    description?: string;
}

// --- Interface Definitions ---

const genderOptions = ['Male', 'Female', 'Other'];
const employeeTypeOptions = ['Full-time', 'Part-time', 'Contract', 'Daily-wage', 'Intern', 'Trainee'];
const phoneLabelOptions = ['Mobile', 'Home', 'Work', 'Emergency'];

const AddEditEmployee: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isEditMode = Boolean(id);
    const navigate = useNavigate();
    const { compressSingleImage } = useImageCompression();
    const [loading, setLoading] = useState(false);
    const [roles, setRoles] = useState<Role[]>([]);
    const [rolesLoading, setRolesLoading] = useState(false);

    // Photo states
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string>('');
    const [photoName, setPhotoName] = useState<string>('');

    // Document photo states
    const [aadhaarFrontFile, setAadhaarFrontFile] = useState<File | null>(null);
    const [aadhaarFrontPreview, setAadhaarFrontPreview] = useState<string>('');
    const [aadhaarFrontName, setAadhaarFrontName] = useState<string>('');
    const [aadhaarBackFile, setAadhaarBackFile] = useState<File | null>(null);
    const [aadhaarBackPreview, setAadhaarBackPreview] = useState<string>('');
    const [aadhaarBackName, setAadhaarBackName] = useState<string>('');
    const [panFile, setPanFile] = useState<File | null>(null);
    const [panPreview, setPanPreview] = useState<string>('');
    const [panName, setPanName] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>();

    // Local state for adding phones and emails
    const [newPhone, setNewPhone] = useState<ContactPhone>({ label: 'Mobile', phone_no: '' });
    const [newEmail, setNewEmail] = useState<string>('');

    const {
        control,
        handleSubmit,
        setValue,
        getValues,
        reset,
        formState: { errors },
    } = useForm<EmployeeFormData>({
        defaultValues: {
            first_name: '',
            last_name: '',
            dob: '',
            gender: 'Male',
            contact: {
                phone: [],
                email: [],
            },
            address: {
                line1: '',
                city: '',
                state: '',
                country: '',
                pin_code: '',
            },
            job: {
                joining_date: '',
                employee_type: 'Full-time',
                base_salary: '',
            },
            document: {
                aadhaar: {
                    aadhaar_no: '',
                },
                pan: {
                    pan_no: '',
                }
            },
            finance: {
                bank_ac_no: '',
                ifsc_code: '',
            },
            username: '',
            role: '',
        },
    });

    // Fetch roles from API
    const fetchRoles = useCallback(async () => {
        try {
            setRolesLoading(true);
            const response = await request({
                url: 'role/get-all-roles',
                method: 'get',
            });

            const rolesData = response.data || response.roles || response;
            setRoles(Array.isArray(rolesData) ? rolesData : []);
        } catch (error: any) {
            console.error('Error fetching roles:', error);
            toast.error(error?.message || 'Failed to fetch roles');
        } finally {
            setRolesLoading(false);
        }
    }, []);

    const getFullImageUrl = (imagePath: string | undefined): string => {
        if (!imagePath) return '';
        if (imagePath.startsWith('http') || imagePath.startsWith('data:')) {
            return imagePath;
        }
        return `${baseUrlImage}${imagePath}`;
    };

    // Fetch employee data if in edit mode
    const fetchEmployeeData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await request({
                url: `employee/get-single-employee/${id}`,
                method: 'get',
            });

            if (res.data) {
                const employeeData = res.data;

                // Format the data for form
                const formattedData: EmployeeFormData = {
                    first_name: employeeData.first_name || '',
                    last_name: employeeData.last_name || '',
                    dob: employeeData.dob ? new Date(employeeData.dob).toISOString().split('T')[0] : '',
                    gender: employeeData.gender || 'Male',
                    photo: employeeData.photo || '',
                    contact: {
                        phone: employeeData.contact?.phone || [],
                        email: employeeData.contact?.email || [],
                    },
                    address: {
                        line1: employeeData.address?.line1 || '',
                        city: employeeData.address?.city || '',
                        state: employeeData.address?.state || '',
                        country: employeeData.address?.country || '',
                        pin_code: employeeData.address?.pin_code || '',
                    },
                    job: {
                        joining_date: employeeData.job?.joining_date ?
                            new Date(employeeData.job.joining_date).toISOString().split('T')[0] : '',
                        employee_type: employeeData.job?.employee_type || 'Full-time',
                        base_salary: employeeData.job?.base_salary?.toString() || '',
                    },
                    document: {
                        aadhaar: {
                            aadhaar_no: employeeData.document?.aadhaar?.aadhaar_no?.toString() || '',
                            aadhaar_photo_front: employeeData.document?.aadhaar?.aadhaar_photo_front || '',
                            aadhaar_photo_back: employeeData.document?.aadhaar?.aadhaar_photo_back || ''
                        },
                        pan: {
                            pan_no: employeeData.document?.pan?.pan_no?.toString() || '',
                            pan_photo: employeeData.document?.pan?.pan_photo || ''
                        }
                    },
                    finance: {
                        bank_ac_no: employeeData.finance?.bank_ac_no?.toString() || '',
                        ifsc_code: employeeData.finance?.ifsc_code?.toString() || '',
                    },
                    username: employeeData.userId?.userName || '',
                    role: employeeData.userId?.role?._id || employeeData.role || '',
                };

                reset(formattedData);

                // Set photo previews with full URL
                setPhotoPreview(getFullImageUrl(employeeData.photo));
                if (employeeData.photo) {
                    const urlParts = employeeData.photo.split('/');
                    const filename = urlParts[urlParts.length - 1];
                    setPhotoName(filename);
                }

                // Set Aadhaar front photo preview
                setAadhaarFrontPreview(getFullImageUrl(employeeData.document?.aadhaar?.aadhaar_photo_front));
                if (employeeData.document?.aadhaar?.aadhaar_photo_front) {
                    const urlParts = employeeData.document.aadhaar.aadhaar_photo_front.split('/');
                    const filename = urlParts[urlParts.length - 1];
                    setAadhaarFrontName(filename);
                }

                // Set Aadhaar back photo preview
                setAadhaarBackPreview(getFullImageUrl(employeeData.document?.aadhaar?.aadhaar_photo_back));
                if (employeeData.document?.aadhaar?.aadhaar_photo_back) {
                    const urlParts = employeeData.document.aadhaar.aadhaar_photo_back.split('/');
                    const filename = urlParts[urlParts.length - 1];
                    setAadhaarBackName(filename);
                }

                // Set PAN photo preview
                setPanPreview(getFullImageUrl(employeeData.document?.pan?.pan_photo));
                if (employeeData.document?.pan?.pan_photo) {
                    const urlParts = employeeData.document.pan.pan_photo.split('/');
                    const filename = urlParts[urlParts.length - 1];
                    setPanName(filename);
                }
            }
        } catch (error: any) {
            console.error('Fetch employee error:', error);
            toast.error(error || 'Failed to fetch employee data');
            navigate(-1);
        } finally {
            setLoading(false);
        }
    }, [id, navigate, reset]);

    // Fetch roles on component mount
    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    // Fetch employee data if in edit mode
    useEffect(() => {
        if (isEditMode) {
            fetchEmployeeData();
        }
    }, [id, fetchEmployeeData, isEditMode]);

    // Photo upload handlers
    const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size should be less than 5MB');
                return;
            }

            if (!file.type.startsWith('image/')) {
                toast.error('Please upload an image file');
                return;
            }

            try {
                // Compress image before upload
                const compressedFile = await compressSingleImage(file);
                setPhotoFile(compressedFile);
                setPhotoName(compressedFile.name);

                const reader = new FileReader();
                reader.onloadend = () => {
                    setPhotoPreview(reader.result as string);
                };
                reader.readAsDataURL(compressedFile);
            } catch (error) {
                console.error('Compression error:', error);
                toast.error('Failed to compress image');
            }
        }
    };

    const handleAadhaarFrontUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size should be less than 5MB');
                return;
            }

            if (!file.type.startsWith('image/')) {
                toast.error('Please upload an image file');
                return;
            }

            try {
                const compressedFile = await compressSingleImage(file);
                setAadhaarFrontFile(compressedFile);
                setAadhaarFrontName(compressedFile.name);

                const reader = new FileReader();
                reader.onloadend = () => {
                    setAadhaarFrontPreview(reader.result as string);
                };
                reader.readAsDataURL(compressedFile);
            } catch (error) {
                console.error('Compression error:', error);
                toast.error('Failed to compress image');
            }
        }
    };

    const handleAadhaarBackUpload =async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size should be less than 5MB');
                return;
            }

            if (!file.type.startsWith('image/')) {
                toast.error('Please upload an image file');
                return;
            }

            try {
                const compressedFile = await compressSingleImage(file);
                setAadhaarBackFile(compressedFile);
                setAadhaarBackName(compressedFile.name);

                const reader = new FileReader();
                reader.onloadend = () => {
                    setAadhaarBackPreview(reader.result as string);
                };
                reader.readAsDataURL(compressedFile);
            } catch (error) {
                console.error('Compression error:', error);
                toast.error('Failed to compress image');
            }
        }
    };

    const handlePanUpload =async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size should be less than 5MB');
                return;
            }

            if (!file.type.startsWith('image/')) {
                toast.error('Please upload an image file');
                return;
            }

            try {
                const compressedFile = await compressSingleImage(file);
                setPanFile(compressedFile);
                setPanName(compressedFile.name);

                const reader = new FileReader();
                reader.onloadend = () => {
                    setPanPreview(reader.result as string);
                };
                reader.readAsDataURL(compressedFile);
            } catch (error) {
                console.error('Compression error:', error);
                toast.error('Failed to compress image');
            }
        }
    };

    const removePhoto = () => {
        setPhotoFile(null);
        setPhotoPreview('');
        setPhotoName('');
        setValue('photo', '');
    };

    const removeAadhaarFrontPhoto = () => {
        setAadhaarFrontFile(null);
        setAadhaarFrontPreview('');
        setAadhaarFrontName('');
        setValue('document.aadhaar.aadhaar_photo_front', '');
    };

    const removeAadhaarBackPhoto = () => {
        setAadhaarBackFile(null);
        setAadhaarBackPreview('');
        setAadhaarBackName('');
        setValue('document.aadhaar.aadhaar_photo_back', '');
    };

    const removePanPhoto = () => {
        setPanFile(null);
        setPanPreview('');
        setPanName('');
        setValue('document.pan.pan_photo', '');
    };

    // Phone chip management
    const handleAddPhone = () => {
        if (!newPhone.phone_no.trim()) {
            toast.error('Please enter a phone number');
            return;
        }

        if (!/^[0-9]{10}$/.test(newPhone.phone_no)) {
            toast.error('Invalid phone number (10 digits required)');
            return;
        }

        const currentPhones = getValues('contact.phone');
        const isDuplicate = currentPhones.some(p => p.phone_no === newPhone.phone_no);

        if (isDuplicate) {
            toast.error('This phone number already exists');
            return;
        }

        setValue('contact.phone', [...currentPhones, { ...newPhone }]);
        setNewPhone({ label: 'Mobile', phone_no: '' });
    };

    const handlePhoneKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddPhone();
        }
    };

    const handleRemovePhone = (index: number) => {
        const currentPhones = getValues('contact.phone');
        const updatedPhones = currentPhones.filter((_, i) => i !== index);
        setValue('contact.phone', updatedPhones);
    };

    // Email chip management
    const handleAddEmail = () => {
        if (!newEmail.trim()) {
            toast.error('Please enter an email address');
            return;
        }

        if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(newEmail)) {
            toast.error('Invalid email address');
            return;
        }

        const currentEmails = getValues('contact.email');
        const isDuplicate = currentEmails.includes(newEmail);

        if (isDuplicate) {
            toast.error('This email already exists');
            return;
        }

        setValue('contact.email', [...currentEmails, newEmail]);
        setNewEmail('');
    };

    const handleEmailKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddEmail();
        }
    };

    const handleRemoveEmail = (index: number) => {
        const currentEmails = getValues('contact.email');
        const updatedEmails = currentEmails.filter((_, i) => i !== index);
        setValue('contact.email', updatedEmails);
    };

    const onSubmit = async (data: EmployeeFormData) => {
        try {
            setLoading(true);

            // Validation
            if (!data.contact.phone || data.contact.phone.length === 0) {
                toast.error("At least one phone number is required");
                return;
            }

            if (!data.contact.email || data.contact.email.length === 0) {
                toast.error("At least one email address is required");
                return;
            }

            // Validation for username (always required)
            if (!data.username) {
                toast.error("Username is required");
                return;
            }

            // Validation for role (always required)
            if (!data.role) {
                toast.error("Role is required");
                return;
            }

            const cleanUrl = (value?: string) =>
                value && value !== "undefined" && value !== "null" ? value : "";

            const formattedData: any = {
                first_name: data.first_name,
                last_name: data.last_name,
                dob: data.dob ? new Date(data.dob).toISOString() : null,
                gender: data.gender,
                username: data.username,
                role: data.role,
                photo: cleanUrl(data.photo),
                contact: {
                    phone: data.contact.phone,
                    email: data.contact.email || [],
                },
                address: {
                    line1: data.address.line1,
                    city: data.address.city,
                    state: data.address.state,
                    country: data.address.country,
                    pin_code: data.address.pin_code,
                },
                job: {
                    joining_date: data.job.joining_date
                        ? new Date(data.job.joining_date).toISOString()
                        : null,
                    employee_type: data.job.employee_type,
                    base_salary: Number(data.job.base_salary) || 0,
                },
                document: {
                    aadhaar: {
                        aadhaar_no: data.document?.aadhaar?.aadhaar_no || "",
                        aadhaar_photo_front: typeof data.document?.aadhaar?.aadhaar_photo_front === "string"
                            ? data.document.aadhaar.aadhaar_photo_front
                            : "",
                        aadhaar_photo_back: typeof data.document?.aadhaar?.aadhaar_photo_back === "string"
                            ? data.document.aadhaar.aadhaar_photo_back
                            : "",
                    },
                    pan: {
                        pan_no: data.document?.pan?.pan_no || "",
                        pan_photo: typeof data.document?.pan?.pan_photo === "string"
                            ? data.document.pan.pan_photo
                            : "",
                    },
                },
                finance: {
                    bank_ac_no: data.finance?.bank_ac_no || "",
                    ifsc_code: data.finance?.ifsc_code || "",
                },
            };

            // ⚠️ IMPORTANT: Remove password from payload in edit mode
            // Only include password for create mode
            if (!isEditMode) {
                // For create mode, password is required
                if (!data.password) {
                    toast.error("Password is required");
                    setLoading(false);
                    return;
                }
                if (data.password.length < 6) {
                    toast.error("Password must be at least 6 characters");
                    setLoading(false);
                    return;
                }
                formattedData.password = data.password;
            }

            // FormData
            const formData = new FormData();
            formData.append("data", JSON.stringify(formattedData));

            // REAL FILES ONLY
            if (photoFile instanceof File) {
                formData.append("photo_file", photoFile);
            }

            if (aadhaarFrontFile instanceof File) {
                formData.append("aadhaar_front_file", aadhaarFrontFile);
            }

            if (aadhaarBackFile instanceof File) {
                formData.append("aadhaar_back_file", aadhaarBackFile);
            }

            if (panFile instanceof File) {
                formData.append("pan_file", panFile);
            }

            const url = isEditMode
                ? `employee/update-employee/${id}`
                : `employee/add-employee`;

            const method = isEditMode ? "put" : "post";

            const res = await request({
                url,
                method,
                data: formData,
                headers: { "Content-Type": "multipart/form-data" }
            });

            if (res?.status === "success") {
                toast.success(
                    res.message ||
                    (isEditMode
                        ? "Employee updated successfully"
                        : "Employee added successfully")
                );
                navigate(-1);
            } else {
                toast.error(res?.message || "Operation failed");
            }
        } catch (error: any) {
            console.error("Submit error:", error);
            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                (isEditMode
                    ? "Failed to update employee"
                    : "Failed to add employee")
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Box sx={{ mb: 2 }}>
                <Brandcrumb />
            </Box>

            <Card elevation={4}>
                <CardContent>
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" fontWeight="bold">
                            {isEditMode ? 'Edit Employee' : 'Add New Employee'}
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/shopManagement/employeeManagement/employees")}
                            size="small"
                            sx={{ fontSize: '0.75rem', py: 0.5 }}
                        >
                            Back to List
                        </Button>
                    </Box>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        {/* Basic Information Section */}
                        <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PersonIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                Basic Information
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {/* Photo Upload */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar
                                        src={photoPreview}
                                        sx={{ width: 60, height: 60, border: '1px solid', borderColor: 'divider' }}
                                    >
                                        <PersonIcon fontSize="small" />
                                    </Avatar>
                                    <Box>
                                        <input
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            id="photo-upload"
                                            type="file"
                                            onChange={handlePhotoUpload}
                                        />
                                        <label htmlFor="photo-upload">
                                            <Button
                                                component="span"
                                                size="small"
                                                variant="outlined"
                                                startIcon={<UploadIcon />}
                                                sx={{ fontSize: '0.75rem', py: 0.25 }}
                                            >
                                                Upload Photo
                                            </Button>
                                        </label>
                                        {photoPreview && (
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={removePhoto}
                                                sx={{ ml: 1, fontSize: '0.75rem', py: 0.25 }}
                                            >
                                                Remove
                                            </Button>
                                        )}
                                        {photoName && (
                                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                                File: {photoName}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>

                                {/* Name Fields */}
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Controller
                                            name="first_name"
                                            control={control}
                                            rules={{
                                                required: 'First name is required',
                                                minLength: { value: 2, message: 'Minimum 2 characters' }
                                            }}
                                            render={({ field, fieldState }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    size="small"
                                                    label="First Name *"
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            )}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Controller
                                            name="last_name"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    size="small"
                                                    label="Last Name"
                                                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Controller
                                            name="dob"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    size="small"
                                                    label="Date of Birth"
                                                    type="date"
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                                />
                                            )}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <FormControl fullWidth size="small" error={!!errors.gender}>
                                            <InputLabel sx={{ fontSize: '0.875rem' }}>Gender *</InputLabel>
                                            <Controller
                                                name="gender"
                                                control={control}
                                                rules={{ required: 'Gender is required' }}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        label="Gender *"
                                                        sx={{ fontSize: '0.875rem' }}
                                                    >
                                                        {genderOptions.map((option) => (
                                                            <MenuItem key={option} value={option} sx={{ fontSize: '0.875rem' }}>
                                                                {option}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                            {errors.gender && <FormHelperText sx={{ fontSize: '0.75rem' }}>{errors.gender.message}</FormHelperText>}
                                        </FormControl>
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>

                        {/* User Account Information Section */}
                        <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LockIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                User Account Information <span style={{ color: '#d32f2f', fontSize: '0.75rem', marginLeft: '4px' }}>(Required)</span>
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    {/* Username */}
                                    <Box sx={{ flex: 1 }}>
                                        <Controller
                                            name="username"
                                            control={control}
                                            rules={{
                                                required: 'Username is required',
                                                minLength: { value: 3, message: 'Minimum 3 characters' },
                                                maxLength: { value: 20, message: 'Maximum 20 characters' },
                                                pattern: {
                                                    value: /^[a-zA-Z0-9_]+$/,
                                                    message: 'Only letters, numbers, and underscores allowed'
                                                }
                                            }}
                                            render={({ field, fieldState }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    size="small"
                                                    label="Username *"
                                                    placeholder='Set unique user name'
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                    disabled={isEditMode}
                                                    required
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <PersonIcon fontSize="small" color="action" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            )}
                                        />
                                        {isEditMode && (
                                            <FormHelperText sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>
                                                Username cannot be changed after creation
                                            </FormHelperText>
                                        )}
                                    </Box>

                                    {/* Role */}
                                    <Box sx={{ flex: 1 }}>
                                        <FormControl fullWidth size="small" error={!!errors.role} required>
                                            <InputLabel sx={{ fontSize: '0.875rem' }}>Role *</InputLabel>
                                            <Controller
                                                name="role"
                                                control={control}
                                                rules={{ required: 'Role is required' }}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        label="Role *"
                                                        disabled={isEditMode}
                                                        sx={{ fontSize: '0.875rem' }}
                                                        startAdornment={
                                                            <InputAdornment position="start">
                                                                <RoleIcon fontSize="small" color="action" />
                                                            </InputAdornment>
                                                        }
                                                    >
                                                        {rolesLoading ? (
                                                            <MenuItem disabled value="">
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <CircularProgress size={16} />
                                                                    <Typography variant="body2">Loading roles...</Typography>
                                                                </Box>
                                                            </MenuItem>
                                                        ) : roles.length > 0 ? (
                                                            roles.map((role) => (
                                                                <MenuItem key={role._id} value={role._id} sx={{ fontSize: '0.875rem' }}>
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                        <Typography variant="body2">{role.name}</Typography>
                                                                        {role.description && (
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {role.description}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </MenuItem>
                                                            ))
                                                        ) : (
                                                            <MenuItem disabled value="">
                                                                <Typography variant="body2" color="error">
                                                                    No roles available
                                                                </Typography>
                                                            </MenuItem>
                                                        )}
                                                    </Select>
                                                )}
                                            />
                                            {errors.role && <FormHelperText sx={{ fontSize: '0.75rem' }}>{errors.role.message}</FormHelperText>}
                                        </FormControl>
                                        {isEditMode && (
                                            <FormHelperText sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                                Role cannot be changed after creation
                                            </FormHelperText>
                                        )}
                                    </Box>
                                </Box>

                                {/* Password field - Only for create mode */}
                                {!isEditMode && (
                                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Controller
                                                name="password"
                                                control={control}
                                                rules={{
                                                    required: 'Password is required',
                                                    minLength: { value: 6, message: 'Minimum 6 characters' },
                                                    maxLength: { value: 20, message: 'Maximum 20 characters' },
                                                    pattern: {
                                                        value: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]/,
                                                        message: 'Must contain at least one letter and one number'
                                                    }
                                                }}
                                                render={({ field, fieldState }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        size="small"
                                                        placeholder="Set unique password"
                                                        label="Password *"
                                                        type={showPassword ? 'text' : 'password'}
                                                        error={!!fieldState.error}
                                                        helperText={fieldState.error?.message}
                                                        required
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <VpnKeyIcon fontSize="small" color="action" />
                                                                </InputAdornment>
                                                            ),
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton
                                                                        onClick={() => setShowPassword(!showPassword)}
                                                                        edge="end"
                                                                        size="small"
                                                                    >
                                                                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                )}
                                            />
                                            <FormHelperText sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>
                                                Must contain at least 6 characters with letters and numbers
                                            </FormHelperText>
                                        </Box>
                                        <Box sx={{ flex: 1 }} />
                                    </Box>
                                )}

                                {/* Information alert for edit mode */}
                                {isEditMode && (
                                    <Alert severity="info" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                                        Username and role cannot be changed after creation. Password cannot be updated here.
                                    </Alert>
                                )}
                            </Box>
                        </Paper>

                        {/* Rest of the sections remain the same */}
                        {/* Contact Information Section */}
                        <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                Contact Information
                            </Typography>
                            <Box display="flex" gap={1.5}>
                                <Box flexGrow={1}>
                                    <Typography variant="caption" fontWeight="medium" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                                        Phone Numbers *
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                        <Select
                                            value={newPhone.label}
                                            onChange={(e) => setNewPhone({ ...newPhone, label: e.target.value as string })}
                                            size="small"
                                            sx={{ minWidth: 80, fontSize: '0.75rem' }}
                                        >
                                            {phoneLabelOptions.map((label) => (
                                                <MenuItem key={label} value={label} sx={{ fontSize: '0.75rem' }}>
                                                    {label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <TextField
                                            size="small"
                                            placeholder="Enter phone number"
                                            value={newPhone.phone_no}
                                            onChange={(e) => setNewPhone({ ...newPhone, phone_no: e.target.value })}
                                            onKeyPress={handlePhoneKeyPress}
                                            sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                        />
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={handleAddPhone}
                                            sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 'auto', px: 1 }}
                                        >
                                            Add
                                        </Button>
                                    </Box>
                                    <Controller
                                        name="contact.phone"
                                        control={control}
                                        render={({ field }) => (
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
                                                {field.value.map((phone, index) => (
                                                    <Chip
                                                        key={`phone-${index}-${phone.phone_no}`}
                                                        label={`${phone.label}: ${phone.phone_no}`}
                                                        onDelete={() => handleRemovePhone(index)}
                                                        size="small"
                                                        sx={{ fontSize: '0.7rem', height: 24 }}
                                                    />
                                                ))}
                                            </Stack>
                                        )}
                                    />
                                </Box>

                                <Box flexGrow={1}>
                                    <Typography variant="caption" fontWeight="medium" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                                        Email Addresses <span style={{ color: '#d32f2f' }}>*</span>
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                        <TextField
                                            size="small"
                                            placeholder="Enter email address"
                                            value={newEmail}
                                            onChange={(e) => setNewEmail(e.target.value)}
                                            onKeyPress={handleEmailKeyPress}
                                            sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                        />
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={handleAddEmail}
                                            sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 'auto', px: 1 }}
                                        >
                                            Add
                                        </Button>
                                    </Box>
                                    <Controller
                                        name="contact.email"
                                        control={control}
                                        rules={{
                                            validate: (value) => {
                                                if (!value || value.length === 0) {
                                                    return 'At least one email address is required';
                                                }
                                                return true;
                                            }
                                        }}
                                        render={({ field, fieldState }) => (
                                            <>
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
                                                    {field.value.map((email, index) => (
                                                        <Chip
                                                            key={`email-${index}-${email}`}
                                                            label={email}
                                                            onDelete={() => handleRemoveEmail(index)}
                                                            size="small"
                                                            sx={{ fontSize: '0.7rem', height: 24 }}
                                                        />
                                                    ))}
                                                </Stack>
                                                {fieldState.error && (
                                                    <FormHelperText error sx={{ fontSize: '0.7rem', mt: 0.5 }}>
                                                        {fieldState.error.message}
                                                    </FormHelperText>
                                                )}
                                            </>
                                        )}
                                    />
                                </Box>
                            </Box>
                        </Paper>

                        {/* Job Information Section */}
                        <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <WorkIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                Job Information
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Controller
                                            name="job.joining_date"
                                            control={control}
                                            rules={{ required: 'Joining date is required' }}
                                            render={({ field, fieldState }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    size="small"
                                                    label="Joining Date *"
                                                    type="date"
                                                    InputLabelProps={{ shrink: true }}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                                />
                                            )}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel sx={{ fontSize: '0.875rem' }}>Employee Type</InputLabel>
                                            <Controller
                                                name="job.employee_type"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        label="Employee Type"
                                                        sx={{ fontSize: '0.875rem' }}
                                                    >
                                                        {employeeTypeOptions.map((type) => (
                                                            <MenuItem key={type} value={type} sx={{ fontSize: '0.875rem' }}>
                                                                {type}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                        </FormControl>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <Box sx={{ flex: 1 / 2.02 }}>
                                        <Controller
                                            name="job.base_salary"
                                            control={control}
                                            rules={{ required: 'Base salary is required' }}
                                            render={({ field, fieldState }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    size="small"
                                                    label="Base Salary (per month) *"
                                                    type="number"
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start" sx={{ fontSize: '0.875rem' }}>₹</InputAdornment>,
                                                    }}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>

                        {/* Address Information Section */}
                        <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                Address Information (Optional)
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Controller
                                    name="address.line1"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            label="Address Line 1"
                                            sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                />
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Controller
                                            name="address.city"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    size="small"
                                                    label="City"
                                                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            )}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Controller
                                            name="address.state"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    size="small"
                                                    label="State"
                                                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Controller
                                            name="address.country"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    size="small"
                                                    label="Country"
                                                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            )}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Controller
                                            name="address.pin_code"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    size="small"
                                                    label="PIN Code"
                                                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>

                        {/* Document & Financial Information */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Paper elevation={0} sx={{ p: 1.5, flex: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <BadgeIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                    Documents (Optional)
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Typography variant="caption" fontWeight="medium" sx={{ mt: 1, color: 'text.secondary' }}>
                                        Aadhaar
                                    </Typography>
                                    <Controller
                                        name="document.aadhaar.aadhaar_no"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                size="small"
                                                label="Aadhaar Number"
                                                placeholder="12-digit number"
                                                sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Box sx={{ mt: 1, flexGrow: 1 }}>
                                            <Typography variant="caption" fontWeight="medium" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                                Aadhaar Front Side
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar
                                                    src={aadhaarFrontPreview}
                                                    sx={{ width: 50, height: 50, border: '1px solid', borderColor: 'divider' }}
                                                >
                                                    <BadgeIcon fontSize="small" />
                                                </Avatar>
                                                <Box>
                                                    <input
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        id="aadhaar-front-upload"
                                                        type="file"
                                                        onChange={handleAadhaarFrontUpload}
                                                    />
                                                    <label htmlFor="aadhaar-front-upload">
                                                        <Button
                                                            component="span"
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<UploadIcon />}
                                                            sx={{ fontSize: '0.75rem', py: 0.25 }}
                                                        >
                                                            Upload Front
                                                        </Button>
                                                    </label>
                                                    {aadhaarFrontPreview && (
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            onClick={removeAadhaarFrontPhoto}
                                                            sx={{ ml: 1, fontSize: '0.75rem', py: 0.25 }}
                                                        >
                                                            Remove
                                                        </Button>
                                                    )}
                                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                                        Only image files are allowed
                                                    </Typography>
                                                    {aadhaarFrontName && (
                                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                                            File: {aadhaarFrontName}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Box sx={{ mt: 1, flexGrow: 1 }}>
                                            <Typography variant="caption" fontWeight="medium" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                                Aadhaar Back Side
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar
                                                    src={aadhaarBackPreview}
                                                    sx={{ width: 50, height: 50, border: '1px solid', borderColor: 'divider' }}
                                                >
                                                    <BadgeIcon fontSize="small" />
                                                </Avatar>
                                                <Box>
                                                    <input
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        id="aadhaar-back-upload"
                                                        type="file"
                                                        onChange={handleAadhaarBackUpload}
                                                    />
                                                    <label htmlFor="aadhaar-back-upload">
                                                        <Button
                                                            component="span"
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<UploadIcon />}
                                                            sx={{ fontSize: '0.75rem', py: 0.25 }}
                                                        >
                                                            Upload Back
                                                        </Button>
                                                    </label>
                                                    {aadhaarBackPreview && (
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            onClick={removeAadhaarBackPhoto}
                                                            sx={{ ml: 1, fontSize: '0.75rem', py: 0.25 }}
                                                        >
                                                            Remove
                                                        </Button>
                                                    )}
                                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                                        Only image files are allowed
                                                    </Typography>
                                                    {aadhaarBackName && (
                                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                                            File: {aadhaarBackName}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Typography variant="caption" fontWeight="medium" sx={{ mt: 2, color: 'text.secondary' }}>
                                        PAN
                                    </Typography>
                                    <Controller
                                        name="document.pan.pan_no"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                size="small"
                                                label="PAN Number"
                                                placeholder="10-character"
                                                sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Avatar
                                            src={panPreview}
                                            sx={{ width: 50, height: 50, border: '1px solid', borderColor: 'divider' }}
                                        >
                                            <BadgeIcon fontSize="small" />
                                        </Avatar>
                                        <Box>
                                            <input
                                                accept="image/*"
                                                style={{ display: 'none' }}
                                                id="pan-upload"
                                                type="file"
                                                onChange={handlePanUpload}
                                            />
                                            <label htmlFor="pan-upload">
                                                <Button
                                                    component="span"
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<UploadIcon />}
                                                    sx={{ fontSize: '0.75rem', py: 0.25 }}
                                                >
                                                    Upload PAN
                                                </Button>
                                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                                    Only image files are allowed
                                                </Typography>
                                            </label>
                                            {panPreview && (
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    onClick={removePanPhoto}
                                                    sx={{ ml: 1, fontSize: '0.75rem', py: 0.25 }}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                            {panName && (
                                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                                    File: {panName}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Paper>

                            <Paper elevation={0} sx={{ p: 1.5, flex: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <BankIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                    Bank Details (Optional)
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Controller
                                        name="finance.bank_ac_no"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                size="small"
                                                label="Bank Account Number"
                                                sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="finance.ifsc_code"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                size="small"
                                                label="IFSC Code"
                                                placeholder="11-character code"
                                                sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Paper>
                        </Box>

                        {/* Form Actions */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate(-1)}
                                disabled={loading}
                                size="small"
                                sx={{ fontSize: '0.75rem', py: 0.5, px: 2 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={16} /> : null}
                                size="small"
                                sx={{ fontSize: '0.75rem', py: 0.5, px: 2 }}
                            >
                                {loading ? 'Saving...' : (isEditMode ? 'Update Employee' : 'Add Employee')}
                            </Button>
                        </Box>
                    </form>
                </CardContent>
            </Card>
        </>
    );
};

export default AddEditEmployee;